import React, { useContext, useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import IRMA from "@models/IRMA";
import IRMA_Line_Item from "@models/IRMA_Line_Item";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwTable from "@components/shared/OwTable/OwTable";
import IOwTableCols from "@models/IOwTableCols";
import RmaService from "@services/RmaService";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import "./ReceiveItemsModal.scss";
import NotificationLevelEnum from "@models/NotificationLevelEnum";

type Props = {
    rma: IRMA;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
};

interface ReceiveLineItem extends IRMA_Line_Item {
    receiveNow: number;
}

export const ReceiveItemsModal = (props: Props) => {
    const { rma, isOpen, onClose, onSuccess } = props;
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);

    const [lineItems, setLineItems] = useState<ReceiveLineItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen && rma.RMA_Line_Items) {
            const items: ReceiveLineItem[] = rma.RMA_Line_Items.map((item) => ({
                ...item,
                receiveNow: 0,
            }));
            setLineItems(items);
        }
    }, [isOpen, rma]);

    const handleQuantityChange = (index: number, value: string) => {
        const qty = parseInt(value) || 0;
        const updatedItems = [...lineItems];
        updatedItems[index].receiveNow = qty;
        setLineItems(updatedItems);
    };

    const validateReceive = (item: ReceiveLineItem): { valid: boolean; message?: string } => {
        const alreadyReceived = item.qty_received || 0;
        const willReceive = alreadyReceived + item.receiveNow;

        if (item.receiveNow < 0) {
            return { valid: false, message: "Quantity must be positive" };
        }

        if (willReceive > item.qty_returned) {
            return {
                valid: false,
                message: `Cannot exceed ${item.qty_returned} (would receive ${willReceive} total)`,
            };
        }

        return { valid: true };
    };

    const handleSubmit = async () => {
        const itemsToReceive = lineItems.filter((item) => item.receiveNow > 0);

        if (itemsToReceive.length === 0) {
            uiContext.showNotification({
                message: "Please enter at least one quantity to receive",
                level: NotificationLevelEnum.warning,
            });
            return;
        }

        // Validate all items
        for (const item of itemsToReceive) {
            const validation = validateReceive(item);
            if (!validation.valid) {
                uiContext.showNotification({
                    message: `${item.sku}: ${validation.message}`,
                    level: NotificationLevelEnum.error,
                });
                return;
            }
        }

        try {
            setLoading(true);
            await RmaService.receiveRma(
                rma.guid,
                {
                    receivedItems: itemsToReceive.map((item) => ({
                        lineItemGuid: item.guid,
                        qty_received: item.receiveNow,
                    })),
                },
                userModel,
                supplierModel
            );

            uiContext.showNotification({
                message: "Items received successfully",
                level: NotificationLevelEnum.success,
            });
            onSuccess();
            onClose();
        } catch (err) {
            uiContext.showNotification({
                message: err instanceof Error ? err.message : "Failed to receive items",
                level: NotificationLevelEnum.error,
            });
        } finally {
            setLoading(false);
        }
    };

    const getTotalReceiving = (): number => {
        return lineItems.reduce((sum, item) => sum + item.receiveNow, 0);
    };

    const columns: IOwTableCols[] = [
        {
            title: "sku",
            valueProp: "sku",
            className: "w-20",
            renderFnEmitsRow: true,
            renderFn: (row: ReceiveLineItem) => <span>{row.sku}</span>,
        },
        {
            title: "product",
            valueProp: "catalog_item_name",
            className: "w-25",
            renderFnEmitsRow: true,
            renderFn: (row: ReceiveLineItem) => <span>{row.catalog_item_name}</span>,
        },
        {
            title: "qty_returned",
            valueProp: "qty_returned",
            className: "w-15",
            renderFnEmitsRow: true,
            renderFn: (row: ReceiveLineItem) => <span>{row.qty_returned}</span>,
        },
        {
            title: "already_received",
            className: "w-15",
            valueProp: "qty_received",
            renderFnEmitsRow: true,
            renderFn: (row: ReceiveLineItem) => <span>{row.qty_received || 0}</span>,
        },
        {
            title: "receive_now",
            valueProp: "receiveNow",
            className: "w-25",
            renderFnEmitsRow: true,
            renderFn: (row: ReceiveLineItem, index?: number) => {
                const validation = validateReceive(row);
                return (
                    <div>
                        <OwInputWrapper
                            name="receive_now"
                            type="number"
                            value={row.receiveNow.toString()}
                            fn={(e: any) => handleQuantityChange(index!, e.target.value)}
                            min={0}
                            max={row.qty_returned - (row.qty_received || 0)}
                        />
                        {!validation.valid && row.receiveNow > 0 && <small className="text-danger">{validation.message}</small>}
                    </div>
                );
            },
        },
    ];

    return (
        <Modal show={isOpen} onHide={onClose} size="xl" className="ReceiveItemsModal" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <OwLabel title="receive_items" /> - {rma.rma_number}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted mb-3">
                    <OwLabel title="receive_items_description" />
                </p>

                <OwTable
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataSet={lineItems}
                    columns={columns}
                    dataFetch={false}
                    initialData={false}
                />

                <div className="mt-3">
                    <strong>
                        <OwLabel title="total_items_in_shipment" />: {getTotalReceiving()}
                    </strong>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <OwButton title="cancel" fn={onClose} active={loading} className="btn-secondary me-2" />
                <OwButton title="record_receipt" fn={handleSubmit} active={loading} className="btn-primary me-2" />
            </Modal.Footer>
        </Modal>
    );
};
